import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { ClausulaContrato } from '@/lib/contratos/plantillasPorDefecto';
import type { TipoContrato } from './usePlantillasContratos';

export const BUCKET_CONTRATOS = 'contratos-mantenimiento';

let bucketEnsurePromise: Promise<void> | null = null;

export interface ContratoMantenimiento {
  id: string;
  empresa_id: string;
  cliente_id: string;
  tipo: TipoContrato;
  numero: number;
  numero_formateado: string;
  fecha_inicio: string;
  fecha_fin: string;
  duracion_meses: number;
  renovacion_automatica: boolean;
  periodicidad_pago: 'anual' | 'semestral' | 'trimestral' | 'mensual';
  precio_total: number;
  precio_periodo: number;
  iva_incluido: boolean;
  sistemas_ids: string[];
  objeto: string;
  clausulas: ClausulaContrato[];
  texto_legal: string;
  observaciones: string | null;
  estado: 'borrador' | 'pendiente_firma' | 'vigente' | 'vencido' | 'rescindido';
  firma_cliente: string | null;
  firma_empresa: string | null;
  nombre_firmante_cliente: string | null;
  fecha_firma: string | null;
  pdf_path: string | null;
  created_by: string | null;
  token_firma: string | null;
  token_firma_expira_at: string | null;
  firma_enviado_at: string | null;
  firma_email_destinatario: string | null;
  firma_origen: 'online' | 'manual' | null;
  fecha_rescision: string | null;
  archivado: boolean;
  created_at: string;
  updated_at: string;
}

export interface NuevoContratoInput {
  cliente_id: string;
  tipo: TipoContrato;
  fecha_inicio: string;
  duracion_meses: number;
  renovacion_automatica: boolean;
  periodicidad_pago: 'anual' | 'semestral' | 'trimestral' | 'mensual';
  precio_total: number;
  iva_incluido: boolean;
  sistemas_ids: string[];
  objeto: string;
  clausulas: ClausulaContrato[];
  texto_legal: string;
  observaciones?: string;
  estado: 'borrador' | 'vigente';
  nombre_firmante_cliente?: string;
}

const PERIODOS_ANUALES: Record<string, number> = {
  anual: 1,
  semestral: 2,
  trimestral: 4,
  mensual: 12,
};

export function calcularPrecioPeriodo(
  precioTotal: number,
  periodicidad: 'anual' | 'semestral' | 'trimestral' | 'mensual',
): number {
  const divisor = PERIODOS_ANUALES[periodicidad] ?? 1;
  return Math.round((precioTotal / divisor) * 100) / 100;
}

function calcularFechaFin(inicio: string, meses: number): string {
  const d = new Date(inicio);
  d.setMonth(d.getMonth() + meses);
  return d.toISOString().slice(0, 10);
}

export function useContratosMantenimiento(clienteId?: string) {
  const { perfil, user } = useAuth();
  const { toast } = useToast();
  const [contratos, setContratos] = useState<ContratoMantenimiento[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    let q = (supabase as any)
      .from('contratos_mantenimiento')
      .select('*')
      .eq('empresa_id', perfil.empresa_id)
      .order('created_at', { ascending: false });
    if (clienteId) q = q.eq('cliente_id', clienteId);
    const { data, error } = await q;
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setContratos((data ?? []) as ContratoMantenimiento[]);
    }
    setLoading(false);
  }, [perfil?.empresa_id, clienteId, toast]);

  useEffect(() => { load(); }, [load]);

  const asegurarBucketContratos = useCallback(async () => {
    if (!user?.id) throw new Error('Sin sesión');
    if (!bucketEnsurePromise) {
      bucketEnsurePromise = supabase.functions
        .invoke('ensure-contratos-bucket', { body: {} })
        .then(({ error, data }) => {
          if (error) throw error;
          if ((data as any)?.error) throw new Error((data as any).error);
        })
        .catch((error) => {
          bucketEnsurePromise = null;
          throw error;
        });
    }
    await bucketEnsurePromise;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !perfil?.empresa_id) return;
    asegurarBucketContratos().catch((error) => {
      console.warn('No se pudo inicializar el bucket de contratos:', error);
    });
  }, [asegurarBucketContratos, perfil?.empresa_id, user?.id]);

  /** Activa la cobertura de revisiones de los sistemas indicados. */
  const activarCoberturaSistemas = async (sistemasIds: string[]) => {
    if (!sistemasIds.length) return;
    const { error } = await supabase
      .from('sistemas')
      .update({ cobertura_omitida: false })
      .in('id', sistemasIds)
      .eq('cobertura_omitida', true);
    if (error) console.error('Error activando cobertura:', error);
  };

  const crearContrato = async (input: NuevoContratoInput): Promise<ContratoMantenimiento> => {
    if (!perfil?.empresa_id || !user?.id) throw new Error('Sin sesión');

    // 1. Generar número
    const { data: numData, error: numErr } = await (supabase as any).rpc('generar_numero_contrato', {
      _empresa_id: perfil.empresa_id,
      _tipo: input.tipo,
    });
    if (numErr) throw numErr;
    const { numero, numero_formateado } = numData[0];

    // 2. Calcular fecha fin y precio periódico
    const fecha_fin = calcularFechaFin(input.fecha_inicio, input.duracion_meses);
    const precio_periodo = calcularPrecioPeriodo(input.precio_total, input.periodicidad_pago);

    const payload: any = {
      empresa_id: perfil.empresa_id,
      cliente_id: input.cliente_id,
      tipo: input.tipo,
      numero,
      numero_formateado,
      fecha_inicio: input.fecha_inicio,
      fecha_fin,
      duracion_meses: input.duracion_meses,
      renovacion_automatica: input.renovacion_automatica,
      periodicidad_pago: input.periodicidad_pago,
      precio_total: input.precio_total,
      precio_periodo,
      iva_incluido: input.iva_incluido,
      sistemas_ids: input.sistemas_ids,
      objeto: input.objeto,
      clausulas: input.clausulas,
      texto_legal: input.texto_legal,
      observaciones: input.observaciones || null,
      estado: input.estado,
      nombre_firmante_cliente: input.nombre_firmante_cliente || null,
      created_by: user.id,
    };

    const { data, error } = await (supabase as any)
      .from('contratos_mantenimiento')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;

    // 3. Activar cobertura si vigente
    if (input.estado === 'vigente' && input.sistemas_ids.length > 0) {
      await activarCoberturaSistemas(input.sistemas_ids);
    }

    await load();
    return data as ContratoMantenimiento;
  };

  const actualizarContrato = async (id: string, patch: Partial<ContratoMantenimiento>) => {
    const { error } = await (supabase as any)
      .from('contratos_mantenimiento')
      .update(patch)
      .eq('id', id);
    if (error) throw error;
    // Si pasa a vigente, activar cobertura
    if (patch.estado === 'vigente') {
      const c = contratos.find(x => x.id === id);
      if (c) await activarCoberturaSistemas(patch.sistemas_ids ?? c.sistemas_ids);
    }
    await load();
  };

  const eliminarContrato = async (c: ContratoMantenimiento) => {
    if (c.pdf_path) {
      await asegurarBucketContratos();
      const { error: removeError } = await supabase.storage.from(BUCKET_CONTRATOS).remove([c.pdf_path]);
      if (removeError) throw removeError;
    }
    const { error } = await (supabase as any)
      .from('contratos_mantenimiento')
      .delete()
      .eq('id', c.id);
    if (error) throw error;
    await load();
  };

  const subirPdf = async (c: ContratoMantenimiento, blob: Blob): Promise<string> => {
    if (!perfil?.empresa_id) throw new Error('Sin empresa');
    await asegurarBucketContratos();
    const path = `${perfil.empresa_id}/${c.id}.pdf`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET_CONTRATOS)
      .upload(path, blob, { upsert: true, contentType: 'application/pdf' });
    if (upErr) {
      toast({ title: 'Error al subir PDF', description: upErr.message, variant: 'destructive' });
      throw upErr;
    }
    const { error: updateError } = await (supabase as any)
      .from('contratos_mantenimiento')
      .update({ pdf_path: path })
      .eq('id', c.id);
    if (updateError) throw updateError;
    await load();
    return path;
  };

  /**
   * En móvil/tablet los navegadores bloquean window.open si se llama después
   * de un await (se pierde el gesto del usuario). Abrimos la pestaña de forma
   * síncrona y luego le asignamos la URL firmada.
   */
  const abrirEnPestana = (): Window | null => {
    try {
      return window.open('', '_blank');
    } catch {
      return null;
    }
  };

  const navegarAUrl = (win: Window | null, url: string) => {
    if (win && !win.closed) {
      win.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  const descargarPdf = async (c: ContratoMantenimiento) => {
    if (!c.pdf_path) {
      toast({ title: 'Sin PDF', description: 'Este contrato aún no tiene PDF generado.' });
      return;
    }
    const win = abrirEnPestana();
    try {
      await asegurarBucketContratos();
      const { data, error } = await supabase.storage
        .from(BUCKET_CONTRATOS)
        .createSignedUrl(c.pdf_path, 3600, { download: `${c.numero_formateado}.pdf` });
      if (error || !data) throw error ?? new Error('No se pudo generar el enlace de descarga');
      navegarAUrl(win, data.signedUrl);
    } catch (error: any) {
      if (win && !win.closed) win.close();
      toast({ title: 'Error al descargar PDF', description: error?.message, variant: 'destructive' });
    }
  };

  const verPdf = async (c: ContratoMantenimiento) => {
    if (!c.pdf_path) {
      toast({ title: 'Sin PDF', description: 'Este contrato aún no tiene PDF generado. Pulsa "Regenerar PDF".' });
      return;
    }
    const win = abrirEnPestana();
    try {
      await asegurarBucketContratos();
      const { data, error } = await supabase.storage
        .from(BUCKET_CONTRATOS)
        .createSignedUrl(c.pdf_path, 3600);
      if (error || !data) throw error ?? new Error('No se pudo generar el enlace de visualización');
      navegarAUrl(win, data.signedUrl);
    } catch (error: any) {
      if (win && !win.closed) win.close();
      toast({ title: 'Error al abrir PDF', description: error?.message, variant: 'destructive' });
    }
  };

  const compartirPdf = async (c: ContratoMantenimiento) => {
    if (!c.pdf_path) {
      toast({ title: 'Sin PDF', description: 'Genera primero el PDF con "Regenerar PDF".' });
      return;
    }
    try {
      await asegurarBucketContratos();
      const { data, error } = await supabase.storage
        .from(BUCKET_CONTRATOS)
        .createSignedUrl(c.pdf_path, 60 * 60 * 24 * 7);
      if (error || !data) throw error ?? new Error('No se pudo generar el enlace para compartir');
      const url = data.signedUrl;
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({
          title: `Contrato ${c.numero_formateado}`,
          text: `Contrato de mantenimiento ${c.numero_formateado}`,
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast({ title: 'Enlace copiado', description: 'Válido durante 7 días.' });
    } catch (error: any) {
      toast({ title: 'Error al compartir PDF', description: error?.message, variant: 'destructive' });
    }
  };

  /** Genera o renueva el enlace público de firma del contrato. */
  const generarLinkFirma = async (
    c: ContratoMantenimiento,
    opciones: { email?: string | null; diasValidez?: number } = {},
  ): Promise<{ url: string; token: string; expira_at: string }> => {
    const { data, error } = await supabase.functions.invoke('generar-link-firma-contrato', {
      body: {
        contrato_id: c.id,
        email: opciones.email ?? null,
        dias_validez: opciones.diasValidez ?? 30,
      },
    });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    await load();
    return data as { url: string; token: string; expira_at: string };
  };

  /** Sube un PDF firmado a mano (escaneado/foto) y marca el contrato como vigente. */
  const subirContratoFirmado = async (c: ContratoMantenimiento, file: File): Promise<void> => {
    if (!perfil?.empresa_id) throw new Error('Sin empresa');
    await asegurarBucketContratos();
    const path = `${perfil.empresa_id}/${c.id}.pdf`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET_CONTRATOS)
      .upload(path, file, { upsert: true, contentType: file.type || 'application/pdf' });
    if (upErr) throw upErr;

    const { error: updErr } = await (supabase as any)
      .from('contratos_mantenimiento')
      .update({
        pdf_path: path,
        estado: 'vigente',
        firma_origen: 'manual',
        fecha_firma: new Date().toISOString(),
        token_firma: null,
        token_firma_expira_at: null,
      })
      .eq('id', c.id);
    if (updErr) throw updErr;

    if (c.sistemas_ids?.length) {
      await activarCoberturaSistemas(c.sistemas_ids);
    }
    await load();
  };

  return {
    contratos,
    loading,
    reload: load,
    crearContrato,
    actualizarContrato,
    eliminarContrato,
    subirPdf,
    descargarPdf,
    verPdf,
    compartirPdf,
    activarCoberturaSistemas,
    generarLinkFirma,
    subirContratoFirmado,
  };
}
